/**
 * Demo Simulation Service
 * Performs the simulated action for each demo step when the Next button is clicked.
 * Eliminates the need for the user to manually interact with highlighted elements.
 */
class DemoSimulationService {
  /**
   * Execute the simulated action defined on a demo step.
   *
   * Supported action types:
   * - 'navigate': push a route using Vue Router (no-op if already on that route)
   * - 'click': programmatically click a DOM element by CSS selector
   *
   * A missing or unrecognised simulateAction is treated as a no-op so the step
   * still advances cleanly.
   *
   * @param {Object} step - Current demo step configuration
   * @param {Object} router - Vue Router instance
   * @returns {Promise<void>}
   */
  async simulateStepAction(step, router, choice) {
    // Support both a single simulateAction and an array of simulateActions
    const actions = step.simulateActions
      ? step.simulateActions
      : step.simulateAction
      ? [step.simulateAction]
      : [];

    for (const action of actions) {
      await this._runAction(action, step.id, router, choice);
    }
  }

  async _runAction(action, stepId, router, choice) {
    switch (action.type) {
      case 'navigate':
        await this._navigate(action.route, router);
        break;

      case 'click':
        this._click(action.selector, stepId);
        break;

      case 'polygon-click':
        this._clickPolygon(stepId);
        break;

      case 'select':
        this._select(action.selector, action.choice ?? choice);
        break;

      case 'type-text':
        this._typeText(action.selector, action.value);
        break;

      case 'highlight-text':
        console.log(`[DemoSimulationService] Case highlight-text: selector="${action.selector}", value="${action.value}"`);
        this._highlightText(action.selector, action.value);
        break;

      default:
        console.warn(
          `[DemoSimulationService] Unknown action type "${action.type}" on step "${stepId}"`
        );
    }
  }

  /**
   * Push a new route via Vue Router.
   * Silently skips navigation if already on the target route.
   *
   * @private
   * @param {string} route - Target route path
   * @param {Object} router - Vue Router instance
   * @returns {Promise<void>}
   */
  async _navigate(route, router) {
    if (!router) {
      console.warn('[DemoSimulationService] Router unavailable — skipping navigation');
      return;
    }

    const currentPath = router.currentRoute?.value?.path ?? router.currentRoute?.path;
    if (currentPath === route) {
      return;
    }

    try {
      await router.push(route);
    } catch (error) {
      if (error?.name !== 'NavigationDuplicated') {
        console.warn(`[DemoSimulationService] Navigation to "${route}" failed:`, error.message);
      }
    }
  }

  /**
   * Programmatically click a DOM element matched by selector.
   * Logs a warning and continues if the element is not found.
   *
   * @private
   * @param {string} selector - CSS selector for the target element
   * @param {string} stepId - Step ID used in warning messages
   */
  _click(selector, stepId) {
    const element = document.querySelector(selector);

    if (!element) {
      console.warn(
        `[DemoSimulationService] Element "${selector}" not found for step "${stepId}" — step will still advance`
      );
      return;
    }

    element.click();
  }

  /**
   * Simulate a vocabulary polygon click by dispatching a custom DOM event on window.
   * DocumentReader listens for 'demo:simulate-polygon-click' and calls onPolygonClicked
   * with the first available OCR polygon — no iframe access required.
   *
   * @private
   * @param {string} stepId - Step ID used in warning messages
   */
  _clickPolygon(stepId) {
    const viewerEl = document.querySelector('[data-test="viewer-section"]');

    if (!viewerEl) {
      console.warn(
        `[DemoSimulationService] viewer-section not found for step "${stepId}" — step will still advance`
      );
      return;
    }

    window.dispatchEvent(new CustomEvent('demo:simulate-polygon-click', { bubbles: false }));
  }


  /**
   * Type text into a plain input or textarea by setting its value via the native input setter
   * and dispatching an input event to trigger Vue reactivity.
   *
   * @private
   * @param {string} selector - CSS selector for the target element
   * @param {string} value - Text to type
   */
  _typeText(selector, value) {
    const matched = document.querySelector(selector);
    if (!matched) {
      console.warn(
        `[DemoSimulationService] Element "${selector}" not found — step will still advance`
      );
      return;
    }

    // If the matched element is a wrapper, find the inner input/textarea
    const element = (matched.tagName === 'INPUT' || matched.tagName === 'TEXTAREA')
      ? matched
      : matched.querySelector('input, textarea') || matched;

    // Pick the correct native setter based on the actual element type
    const proto = element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;

    const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

    if (nativeSetter) {
      nativeSetter.call(element, value);
    } else {
      element.value = value;
    }

    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
    console.log(`[DemoSimulationService] Typed "${value}" into "${selector}"`);
  }


  /**
   * Find the text `value` inside the element matched by `selector` and highlight it
   * using the browser's Selection API.
   *
   * @private
   * @param {string} selector - CSS selector for the container element
   * @param {string} value - Text to find and highlight
   */
  _highlightText(selector, value) {
    // Always dispatch the custom event first — PDFViewer (iframe-based) listens on window
    // and cannot rely on DOM text walking
    window.dispatchEvent(new CustomEvent('demo:simulate-text-selection', { detail: { text: value }, bubbles: false }));

    const container = document.querySelector(selector);
    if (!container) {
      console.warn(
        `[DemoSimulationService] Element "${selector}" not found — custom event dispatched for PDF fallback`
      );
      return;
    }

    // Walk text nodes inside the container to find the value
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      const index = node.nodeValue.indexOf(value);
      if (index === -1) continue;

      const range = document.createRange();
      range.setStart(node, index);
      range.setEnd(node, index + value.length);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Dispatch mouseup to trigger TextViewer's handleTextSelection listener
      container.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      window.dispatchEvent(new CustomEvent('demo:simulate-text-selection', { detail: { text: value }, bubbles: false }));

      console.log(`[DemoSimulationService] Highlighted "${value}" in "${selector}"`);
      return;
    }

    console.warn(`[DemoSimulationService] Text "${value}" not found inside "${selector}" — step will still advance`);
  }
  /**
   * Simulate a form selection by clicking the element and selecting the matching option.
   * Works with PrimeVue AutoComplete, Vuetify v-select, and native HTML selects.
   *
   * @private
   * @param {string} selector - CSS selector for the target element
   * @param {string|number} choice - The value/label to select
   */
  _select(selector, choice) {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(
        `[DemoSimulationService] Element "${selector}" not found — step will still advance`
      );
      return;
    }

    console.log(
      `[DemoSimulationService] Element "${selector}" found — selecting "${choice}"`
    );

    // For native HTML select elements
    if (element.tagName === 'SELECT') {
      element.value = choice;
      element.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // For PrimeVue AutoComplete and similar components
    // Find the inner input and simulate typing to trigger the @complete event
    const input = element.querySelector('input') || element;
    input.focus();
    input.value = choice;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));

    // Use MutationObserver to wait for the suggestion panel to appear in the DOM
    const observer = new MutationObserver(() => {
      const menuItems = document.querySelectorAll(
        '.p-autocomplete-item, .p-autocomplete-overlay li, .p-autocomplete-panel li, [role="option"]'
      );

      if (!menuItems.length) return;

      console.log(`[DemoSimulationService] Found ${menuItems.length} menu items for choice "${choice}"`);

      for (const item of menuItems) {
        const itemText = item.textContent.trim();
        console.log(`[DemoSimulationService] Checking: "${itemText}"`);

        if (itemText === choice || item.getAttribute('data-value') === choice) {
          console.log(`[DemoSimulationService] Matched and clicking: "${choice}"`);
          observer.disconnect();
          item.click();
          return;
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Fallback: disconnect observer after 3 seconds to avoid memory leaks
    setTimeout(() => {
      observer.disconnect();
      console.warn(`[DemoSimulationService] Timed out waiting for option "${choice}" — step will still advance`);
    }, 3000);
  }

}

let instance = null;

/**
 * Get or create the global DemoSimulationService singleton.
 * @returns {DemoSimulationService}
 */
export function getDemoSimulationService() {
  if (!instance) {
    instance = new DemoSimulationService();
  }
  return instance;
}

export default DemoSimulationService;
